// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import PropTypes, { shape } from 'prop-types';
import {
  Button,
  Collapse,
  Grid,
  Link,
  Typography,
  withStyles,
} from '@material-ui/core';
import { find, map, get } from 'lodash';

import { FormSelect, FormAutoCompleteCombo } from '../../../../components';
import {
  getAvailableApps,
  getKeysResults,
} from '../../../../reducers/application';
import {
  getAPISwaggerData,
} from '../../../../reducers/api';
import {
  fetchApplicationKeys,
  fetchAvailableApps,
} from '../../../../actions/application';
import {
  fetchSwaggerFile,
} from '../../../../actions/api';
import initializeSwaggerUiBundle, { SWAGGER_UI_EL } from './swagger';
import styles from './styles';
import { getI18n } from '../../../../utils/intl';

export const Spec = ({
  classes,
  apiDetails,
  apiSwagger,
  apiUuid,
  applicationAPIKeys,
  availableApps,
  fetchApplicationKeys,
  fetchAvailableApps,
  fetchSwaggerFile,
}) => {

  const intl = getI18n(useIntl());

  useEffect(() => {
    if (apiUuid) {
      fetchAvailableApps({ size: 2000, apiUuid });
      fetchSwaggerFile(apiUuid);
    }
  }, [apiUuid]);

  const [selectedApp, setSelectedApp] = useState('0');
  const [appList, setAppList] = useState([]);
  const [appSearchText, setAppSearchText] = useState('');
  const [selectedAppAPIKey, setSelectedAppAPIKey] = useState('0');
  const [selectionCollapsed, setSelectionCollapsed] = useState(false);

  // Add 'Select Application' at the top of the list
  useEffect(() => {
    if (availableApps) {
      setAppList([
        {
          name: intl.getI18nMessage('label.api.details.spec.select.application'),
          uuid: '0',
        },
        ...availableApps,
      ])
    }
  }, [availableApps]);

  useEffect(() => {
    if (appSearchText === '') {
      setSelectedApp('0');
    }
  }, [appSearchText]);

  useEffect(() => {
    if (selectedApp !== '0') {
      fetchApplicationKeys(selectedApp, 0, 200, 'SECRET');
    }
    setSelectedAppAPIKey('0');
  }, [selectedApp]);

  useEffect(() => {
    if (apiSwagger) {
      initializeSwaggerUiBundle({
        api: apiDetails,
        apiKey: {},
        apiSwagger,
      });
    }
  }, [apiSwagger]);

  useEffect(() => {
    let apiKeyDetails = {};
    if ((selectedApp !== '0') && (selectedAppAPIKey !== '0')) {
      apiKeyDetails = find(applicationAPIKeys, item => item.apiKey === selectedAppAPIKey);
    }
    initializeSwaggerUiBundle({
      api: apiDetails,
      apiKey: apiKeyDetails,
      apiSwagger,
    });

  }, [selectedApp, selectedAppAPIKey]);

  const onFilterByAppChange = (event = { }) => {
    const value = get(event, 'target.value', '');
    value && setAppSearchText(value);
    fetchAvailableApps({ size: 2000, apiUuid, name: value });
  };

  const onAppSelection = (event, value) => {
    const appUuid = get(value, 'uuid', '');
    const appName = get(value, 'name', '');
    setSelectedApp(appUuid);
    setAppSearchText(appName);
  };

  const renderApplicationsDropdown = () => {
    return (<FormAutoCompleteCombo
        fieldContainerClass={classes.selectionDropdown}
        data={appList}
        isMultiple={false}
        filterSelectedOptions={false}
        searchText={appSearchText}
        inputValue={appSearchText}
        onInputChange={onFilterByAppChange}
        onChange={(e, value) => onAppSelection(e, value)}
        id="api-spec-apps-select"
        labelId="api-spec-apps-select-label"
        name={intl.getI18nMessage('label.application')}
        value={selectedApp}
        inputLabel={intl.getI18nMessage('label.api.details.spec.select.application')}
      />);
  };

  const getApiKeys = () => map(applicationAPIKeys,
    item => ({ name: item.name, uuid: item.apiKey }));

  const renderAPIKeysDropdown = () => {
    return (<FormSelect
      fieldContainerClass={classes.selectionDropdown}
      data={getApiKeys()}
      defaultText={intl.getI18nMessage('label.api.details.spec.select.application.apikey')}
      disabled={selectedApp === '0'}
      handleChange={setSelectedAppAPIKey}
      id="api-spec-apps-apikey-select"
      labelId="api-spec-apps-apikey-select-label"
      name={intl.getI18nMessage('label.apikey')}
      noNativeSelect
      value={selectedAppAPIKey}
      helperText={intl.getI18nMessage('label.api.details.spec.select.application.apikey.helpertext')}
    />);
  };

  return (<div>
    <Grid
      container
      className={classes.selectionContainer}
      data-apim-test="api-details-spec-container"
      data-layer7-test="api-details-spec-container"
      id="api-details-spec-container"
      maxWidth={false}
    >
      <Grid item lg={12}>
        <Typography id="api-spec-connection-details" variant="h4">
          {intl.getI18nMessage('label.api.details.authentication.details')}
        </Typography>
        <Button
          classes={{
            root: classes.btnRoot,
            label: classes.btnLabel,
          }}
          className={classes.collapseLink}
          onClick={() => setSelectionCollapsed(!selectionCollapsed)}
        >
          {
            selectionCollapsed ?
            intl.getI18nMessage('label.api.details.authentication.details.show')
            :
            intl.getI18nMessage('label.api.details.authentication.details.hide')
          }
        </Button>
      </Grid>
      <Collapse className={classes.collapse} in={!selectionCollapsed}>
        <Grid
          container
          lg={12}
          spacing={3}
        >
          <Grid item lg={5} xs={12}>
            {renderApplicationsDropdown()}
          </Grid>
          <Grid item lg={5} xs={12}>
            {renderAPIKeysDropdown()}
          </Grid>
          <Grid item lg={2} xs={12}>
            <div>
              <Link
                className={classes.applicationLink}
                href={'/admin/console/applications/add'}
              >
                {intl.getI18nMessage('label.api.details.spec.create.application')}
              </Link>
            </div>
          </Grid>
        </Grid>
      </Collapse>
    </Grid>
    <div id={SWAGGER_UI_EL} />
  </div>);
};

const mapStateToProps = state => ({
  apiSwagger: getAPISwaggerData(state),
  applicationAPIKeys: getKeysResults(state),
  availableApps: getAvailableApps(state),
});

const mapDispatchToProps = {
  fetchApplicationKeys,
  fetchAvailableApps,
  fetchSwaggerFile,
};

Spec.propTypes = {
  apiUuid: PropTypes.string,
  apiDetails: PropTypes.object,
  apiSwagger: PropTypes.object,
  applicationAPIKeys: PropTypes.arrayOf(PropTypes.object),
  availableApps: PropTypes.arrayOf(PropTypes.object),
  classes: shape({}),
  fetchApplicationKeys: PropTypes.func,
  fetchAvailableApps: PropTypes.func,
  fetchSwaggerFile: PropTypes.func,
};

Spec.displayName = 'Spec';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
  )(Spec);
